/**
 * Punchlists list screen.
 *
 * Lists PunchlistRow rows for the authenticated org (GET /api/punchlists). Tapping a
 * row navigates to the detail screen at /(app)/punchlists/[id]. An inline "+ New
 * punchlist" form creates a punchlist by title and pushes into its detail.
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1). No raw style values.
 */
import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listPunchlists,
  createPunchlist,
  type PunchlistRow,
} from '../../src/api/punchlists';
import { Screen, Text, Badge, Card, Button, Field, ListRow } from '../../src/components/ui';
import { theme } from '../../src/theme';
import { extractErrorMessage } from '../../src/lib/format';
import type { StatusKey } from '../../src/theme';

// Punchlist status → badge spec. Statuses: open, in_progress, complete, signed_off, void.
function punchlistBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'signed_off':
      return { key: 'paid', label: 'Signed off' };
    case 'complete':
      return { key: 'paid', label: 'Complete' };
    case 'in_progress':
      return { key: 'inProgress', label: 'In progress' };
    case 'void':
      return { key: 'draft', label: 'Void' };
    default:
      return { key: 'sent', label: 'Open' };
  }
}

export default function PunchlistsScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['punchlists'],
    queryFn: () => listPunchlists(),
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: (body: { title: string }) => createPunchlist(body),
    onSuccess: (punchlist) => {
      void queryClient.invalidateQueries({ queryKey: ['punchlists'] });
      queryClient.setQueryData(['punchlist', punchlist.id], punchlist);
      setTitle('');
      setShowForm(false);
      router.push(`/(app)/punchlists/${punchlist.id}`);
    },
    onError: (err) => setFormError(extractErrorMessage(err, 'Could not create punchlist.')),
  });

  const onCreate = () => {
    setFormError(null);
    if (!title.trim()) {
      setFormError('Enter a title for the punchlist.');
      return;
    }
    createMutation.mutate({ title: title.trim() });
  };

  if (isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading punchlists"
          />
          <Text variant="body" color="secondary">
            Loading punchlists…
          </Text>
        </View>
      </Screen>
    );
  }

  if (isError) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(error, 'Failed to load punchlists.')}
            </Text>
            <Button
              title="Retry"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => refetch()}
              accessibilityLabel="Retry loading punchlists"
            />
          </Card>
        </View>
      </Screen>
    );
  }

  const rows = data ?? [];

  return (
    <Screen scroll>
      {showForm ? (
        <Card>
          <Text variant="bodyStrong">New punchlist</Text>
          <Field
            label="Title"
            placeholder="e.g. Final walkthrough"
            value={title}
            onChangeText={setTitle}
            autoCapitalize="sentences"
            accessibilityLabel="Punchlist title"
          />
          {formError ? (
            <Text variant="small" color="danger" accessibilityRole="alert">
              {formError}
            </Text>
          ) : null}
          <Button
            title="Create punchlist"
            onPress={onCreate}
            loading={createMutation.isPending}
            disabled={createMutation.isPending || !title.trim()}
            accessibilityLabel="Create punchlist"
          />
          <Button
            title="Cancel"
            variant="ghost"
            onPress={() => {
              setShowForm(false);
              setFormError(null);
              setTitle('');
            }}
            accessibilityLabel="Cancel new punchlist"
          />
        </Card>
      ) : (
        <Button
          title="+ New punchlist"
          onPress={() => setShowForm(true)}
          accessibilityLabel="Create a new punchlist"
        />
      )}

      <Text variant="label" color="secondary">
        {rows.length} {rows.length === 1 ? 'punchlist' : 'punchlists'}
      </Text>

      {rows.length === 0 ? (
        <Text variant="body" color="secondary">
          No punchlists yet. Create your first one to get started.
        </Text>
      ) : (
        <View style={styles.list}>
          {rows.map((p: PunchlistRow) => {
            const b = punchlistBadge(p.status);
            return (
              <ListRow
                key={p.id}
                title={p.title}
                subtitle={
                  p.is_gating
                    ? 'Gating · created ' + new Date(p.created_at).toLocaleDateString()
                    : 'Created ' + new Date(p.created_at).toLocaleDateString()
                }
                right={<Badge status={b.key} label={b.label} />}
                onPress={() => router.push(`/(app)/punchlists/${p.id}`)}
              />
            );
          })}
        </View>
      )}
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  list: {
    gap: theme.spacing.sm,
  },
});
